import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type {
  ActivitySubTab,
  FeedTab,
  Notification,
} from '@/api/notifications';
import { useTranslator } from '@/hooks/useTranslator';
import { useMarkAllRead, useMarkNotification } from './useMarkNotification';
import { useNotifications } from './useNotifications';

const GROUP_ORDER = ['today', 'yesterday', 'thisWeek', 'earlier'] as const;

function dateGroup(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);
  const itemDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (itemDay >= today) return 'today';
  if (itemDay >= yesterday) return 'yesterday';
  if (itemDay >= weekAgo) return 'thisWeek';
  return 'earlier';
}

function groupNotifications(items: Notification[]) {
  const map = new Map<string, Notification[]>();
  for (const item of items) {
    const g = dateGroup(item.createdAt);
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(item);
  }
  return GROUP_ORDER.filter(g => map.has(g)).map(g => ({
    group: g,
    items: map.get(g)!,
  }));
}

export function useNotificationsSheet(open: boolean) {
  const { getTranslation: t } = useTranslator({ page: 'notifications' });
  const [tab, setTab] = useState<FeedTab>('activity');
  const [subTab, setSubTab] = useState<ActivitySubTab>('active');
  const [resourceType, setResourceType] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [companySearchInput, setCompanySearchInput] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isLoading } =
    useNotifications({
      tab,
      subTab,
      ...(resourceType ? { resourceType } : {}),
      ...(companySearch ? { companySearch } : {}),
      enabled: open,
    });

  const markMutation = useMarkNotification();
  const markAllMutation = useMarkAllRead();

  const allItems = data?.pages.flatMap(p => p.data) ?? [];
  const grouped = groupNotifications(allItems);
  const unreadCount = allItems.filter(
    n => n.status === 'UNREAD' || n.status === 'PENDING',
  ).length;

  function setSearch(patch: {
    nTab?: FeedTab;
    nSubTab?: ActivitySubTab;
    nType?: string | undefined;
    nCompany?: string | undefined;
  }) {
    if (patch.nTab !== undefined) {
      setTab(patch.nTab);
      setCompanySearchInput('');
    }
    if (patch.nSubTab !== undefined) setSubTab(patch.nSubTab);
    if ('nType' in patch) setResourceType(patch.nType ?? '');
    if ('nCompany' in patch) setCompanySearch(patch.nCompany ?? '');
  }

  function handleCompanySearchChange(val: string) {
    setCompanySearchInput(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setCompanySearch(val), 400);
  }

  async function invalidateAll() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['notifications', tab] }),
      qc.invalidateQueries({ queryKey: ['dashboard', 'tasks'] }),
    ]);
  }

  async function handleMarkAllRead() {
    try {
      await markAllMutation.mutateAsync();
      await invalidateAll();
    } catch {}
  }

  async function handleDismiss(id: string) {
    try {
      await markMutation.mutateAsync({ id, status: 'DISMISSED' });
      await invalidateAll();
    } catch {}
  }

  async function handleReactivate(id: string) {
    try {
      await markMutation.mutateAsync({ id, status: 'PENDING' });
      await invalidateAll();
    } catch {}
  }

  useEffect(() => {
    if (!bottomRef.current || !hasNextPage || isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    obs.observe(bottomRef.current);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    t,
    tab,
    subTab,
    resourceType,
    companySearchInput,
    setSearch,
    allItems,
    grouped,
    unreadCount,
    isLoading,
    isFetchingNextPage,
    isMarkAllPending: markAllMutation.isPending,
    handleCompanySearchChange,
    handleMarkAllRead,
    handleDismiss,
    handleReactivate,
    bottomRef,
  };
}
